import { NewsInsight } from './news';
import { getTimeBasedGreeting } from './speechUtils';

/**
 * Formats a professional daily summary script for text-to-speech
 * Combines all news summaries into one natural-sounding narration
 */
export function formatDailySummary(insights: NewsInsight[], date: string): string {
  if (!insights || insights.length === 0) {
    return "Sorry, there's no news available for today. Check back tomorrow for the latest updates.";
  }

  // Format the date nicely for speech
  const dateObj = new Date(date);
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  // Build the script with natural, conversational flow
  const parts: string[] = [];

  // Natural, friendly introduction with time-based greeting - use date-based selection for consistency
  const dateHash = date.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const greeting = getTimeBasedGreeting();
  const introVariations = [
    `${greeting}. Here's what's happening in the UAE real estate market on ${formattedDate}.`,
    `Welcome to your daily briefing for ${formattedDate}. Let's dive into Todays UAE real estate news.`,
    `Here's your daily update on the UAE property market for ${formattedDate}.`,
  ];
  parts.push(introVariations[dateHash % introVariations.length]);

  // Add each news item with natural transitions
  insights.forEach((insight, index) => {
    // Varied, natural transitions that sound conversational
    // Use deterministic selection based on date and index for consistency with variety
    let transition = '';
    const selectionHash = (dateHash + index) % 100;
    
    if (index === 0) {
      const firstTransitions = [
        'To start with,',
        'First up,',
        'Let\'s begin with,',
        'Starting off,',
      ];
      transition = firstTransitions[selectionHash % firstTransitions.length];
    } else if (index === insights.length - 1) {
      const lastTransitions = [
        'And finally,',
        'Last but not least,',
        'Wrapping up,',
        'To close out,',
      ];
      transition = lastTransitions[selectionHash % lastTransitions.length];
    } else {
      const middleTransitions = [
        'Moving on,',
        'Also,',
        'In other news,',
        'Additionally,',
        'Another development,',
        'Meanwhile,',
      ];
      transition = middleTransitions[selectionHash % middleTransitions.length];
    }
    
    // Clean up and humanize the summary text
    let summary = humanizeText(insight.summary.trim());
    
    // Ensure proper sentence endings
    if (!summary.match(/[.!?]$/)) {
      summary += '.';
    }
    
    parts.push(`${transition} ${summary}`);
  });

  // Natural, friendly closing - use date-based selection for consistency
  const closingVariations = [
    "That's a wrap on Todays market update. Thanks for listening, and we'll see you tomorrow.",
    "That covers everything for today. Stay tuned for more updates tomorrow.",
    "And that's your market briefing for today. Have a great day!",
  ];
  parts.push(closingVariations[dateHash % closingVariations.length]);

  return parts.join(' ');
}

/**
 * Converts numbers to words (0-999)
 */
function numberToWords(num: number): string {
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 
                'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 
                'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  
  if (num === 0) return 'zero';
  if (num < 20) return ones[num];
  if (num < 100) {
    const ten = Math.floor(num / 10);
    const one = num % 10;
    return one > 0 ? `${tens[ten]}-${ones[one]}` : tens[ten];
  }
  if (num < 1000) {
    const hundred = Math.floor(num / 100);
    const remainder = num % 100;
    if (remainder === 0) return `${ones[hundred]} hundred`;
    return `${ones[hundred]} hundred and ${numberToWords(remainder)}`;
  }
  // For numbers >= 1000, convert digit by digit for the integer part
  return num.toString();
}

/**
 * Converts a single digit to word
 */
function digitToWord(digit: string): string {
  const digits = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const num = parseInt(digit, 10);
  return digits[num] || digit;
}

/**
 * Converts monetary values to natural speech
 * Examples: "AED 448.1 B" → "four hundred and forty-eight point one billion AED"
 */
function convertMonetaryValue(match: string, currency: string, number: string, unit: string): string {
  // Parse the number
  const numParts = number.split('.');
  const integerPart = parseInt(numParts[0], 10);
  const decimalPart = numParts[1] ? numParts[1] : '';
  
  // Convert integer part to words (handle up to 999)
  let integerWords = '';
  if (integerPart < 1000) {
    integerWords = numberToWords(integerPart);
  } else {
    // For numbers >= 1000, break them down
    if (integerPart < 1000000) {
      // Thousands
      const thousands = Math.floor(integerPart / 1000);
      const remainder = integerPart % 1000;
      if (remainder === 0) {
        integerWords = `${numberToWords(thousands)} thousand`;
      } else {
        integerWords = `${numberToWords(thousands)} thousand ${numberToWords(remainder)}`;
      }
    } else if (integerPart < 1000000000) {
      // Millions
      const millions = Math.floor(integerPart / 1000000);
      const remainder = integerPart % 1000000;
      if (remainder === 0) {
        integerWords = `${numberToWords(millions)} million`;
      } else if (remainder < 1000) {
        integerWords = `${numberToWords(millions)} million ${numberToWords(remainder)}`;
      } else {
        const thousands = Math.floor(remainder / 1000);
        const thousandsRemainder = remainder % 1000;
        if (thousandsRemainder === 0) {
          integerWords = `${numberToWords(millions)} million ${numberToWords(thousands)} thousand`;
        } else {
          integerWords = `${numberToWords(millions)} million ${numberToWords(thousands)} thousand ${numberToWords(thousandsRemainder)}`;
        }
      }
    } else {
      // Billions - keep it simple for very large numbers
      integerWords = numberToWords(integerPart);
    }
  }
  
  // Convert decimal part to words (digit by digit)
  let decimalWords = '';
  if (decimalPart) {
    const decimalDigits = decimalPart.split('').map(d => digitToWord(d)).join(' ');
    decimalWords = ` point ${decimalDigits}`;
  }
  
  // Convert unit abbreviation
  const unitMap: { [key: string]: string } = {
    'B': 'billion',
    'billion': 'billion',
    'M': 'million',
    'million': 'million',
    'K': 'thousand',
    'thousand': 'thousand',
    'k': 'thousand',
  };
  const unitWord = unitMap[unit.toUpperCase()] || unit;
  
  // Handle currency
  const currencyMap: { [key: string]: string } = {
    'AED': 'AED',
    'USD': 'US dollars',
    '$': 'dollars',
    'EUR': 'euros',
    '€': 'euros',
    'GBP': 'British pounds',
    '£': 'pounds',
  };
  const currencyWord = currency ? (currencyMap[currency] || currency) : '';
  
  // Construct the result - currency before or after based on original position
  let result = '';
  if (currency && match.trim().startsWith(currency)) {
    // Currency first: "AED 448.1 B" → "AED four hundred and forty-eight point one billion"
    result = `${currencyWord} ${integerWords}${decimalWords} ${unitWord}`;
  } else {
    // Currency after or no currency: "448.1 B" → "four hundred and forty-eight point one billion"
    result = `${integerWords}${decimalWords} ${unitWord}${currencyWord ? ' ' + currencyWord : ''}`;
  }
  
  return result.trim();
}

/**
 * Converts formal, written text into more natural, spoken language
 */
function humanizeText(text: string): string {
  // Remove markdown and special formatting
  let humanized = text
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Remove markdown links
    .replace(/#{1,6}\s+/g, '') // Remove markdown headers
    .trim();

  // Convert monetary values to natural speech
  // Pattern: Currency (optional) + Number + Unit (B/M/K)
  // Examples: "AED 448.1 B", "$100M", "1.5 billion", "448.1B AED"
  // Handle currency before number (most common: "AED 448.1 B")
  humanized = humanized.replace(
    /\b([A-Z]{3}|\$|€|£)\s+(\d+\.?\d*)\s*([BMK]|billion|million|thousand)\b/gi,
    (match, currency, number, unit) => {
      return convertMonetaryValue(match, currency || '', number, unit);
    }
  );
  // Handle currency after number (e.g., "448.1B AED")
  humanized = humanized.replace(
    /\b(\d+\.?\d*)\s*([BMK]|billion|million|thousand)\s+([A-Z]{3}|\$|€|£)\b/gi,
    (match, number, unit, currency) => {
      // Skip if already contains words (was already converted)
      if (/[a-z]/.test(match)) return match;
      return convertMonetaryValue(match, currency || '', number, unit);
    }
  );
  // Handle number with unit but no currency (e.g., "1.5 billion")
  humanized = humanized.replace(
    /\b(\d+\.?\d*)\s+([BMK]|billion|million|thousand)\b/gi,
    (match, number, unit) => {
      // Skip if already contains words (was already converted)
      if (/[a-z]/.test(match)) return match;
      return convertMonetaryValue(match, '', number, unit);
    }
  );

  // Convert formal language to more conversational
  const replacements: [RegExp, string][] = [
    // Numbers and percentages - make them sound natural
    [/\b(\d+) percent\b/gi, '$1%'],
    [/\b(\d+) per cent\b/gi, '$1%'],
    
    // Formal phrases to conversational
    [/\bIt is important to note that\b/gi, 'It\'s worth noting that'],
    [/\bIt should be noted that\b/gi, 'Keep in mind that'],
    [/\bFurthermore\b/gi, 'Plus'],
    [/\bMoreover\b/gi, 'What\'s more'],
    [/\bHowever\b/gi, 'But'],
    [/\bNevertheless\b/gi, 'Still'],
    [/\bIn addition\b/gi, 'Also'],
    [/\bAdditionally\b/gi, 'Plus'],
    
    // Remove redundant formal phrases
    [/\bIt is worth mentioning that\b/gi, ''],
    [/\bIt is worth noting that\b/gi, ''],
    
    // Make sentences flow better - lowercase articles in middle of sentences
    [/\bThe market\b/gi, 'the market'],
    [/\bThe UAE\b/gi, 'the UAE'],
    
    // Fix common awkward phrases to sound more natural
    [/\bhas seen\b/gi, 'saw'],
    [/\bhas experienced\b/gi, 'experienced'],
    [/\bhas recorded\b/gi, 'recorded'],
    
    // Make it sound more like speech
    [/\bapproximately\b/gi, 'about'],
    [/\bsignificant\b/gi, 'major'],
    [/\bconsiderable\b/gi, 'significant'],
    [/\bsubstantial\b/gi, 'significant'],
    
    // Remove overly formal connectors
    [/\bIn conclusion\b/gi, ''],
    [/\bTo summarize\b/gi, ''],
  ];

  replacements.forEach(([pattern, replacement]) => {
    humanized = humanized.replace(pattern, replacement);
  });

  // Clean up multiple spaces and punctuation
  humanized = humanized
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.!?])/g, '$1') // Remove space before punctuation
    .replace(/([,.!?])\s*([,.!?])/g, '$1') // Remove duplicate punctuation
    .trim();

  // Ensure proper capitalization (first letter uppercase, rest as is)
  if (humanized.length > 0) {
    humanized = humanized.charAt(0).toUpperCase() + humanized.slice(1);
  }

  return humanized;
}

